/**
 * A claim's conversation, and the box to add to it.
 *
 * One component for both audiences, because it is one conversation. What
 * differs arrives as props rather than as a variant: the desk can mark a
 * message internal, and the customer cannot — `onSend` takes an `internal`
 * flag only when the caller passes `allowInternal`.
 *
 * **An internal note is drawn as one.** Not merely tagged: the desk needs to
 * see at a glance that a line is not something the customer has read, because
 * the mistake this prevents is answering a customer with a sentence that was
 * written about them.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAction } from "@hardauto/core-data";
import { moment } from "@hardauto/core-i18n";
import { Field } from "@hardauto/core-forms";
import { Badge, Button, Card, Checkbox, EmptyState, Textarea, useToast } from "@hardauto/core-ui";

import type { TicketMessage } from "./api";
import { NAMESPACE } from "./i18n";
import "./screens/support.css";

export interface ThreadProps {
  messages: readonly TicketMessage[];
  /** Sends a reply. Resolves when it has been recorded. */
  onSend?: ((body: string, options: { internal: boolean }) => Promise<unknown>) | undefined;
  /** The desk may write a note to itself; a customer may not. */
  allowInternal?: boolean;
  /** Rendered above the box — a notice about a refused claim, say. */
  notice?: React.ReactNode;
  /** Overrides the send button's label on the portal, where it is "Envoyer". */
  sendLabel?: string;
  placeholder?: string;
}

export function Thread({
  messages,
  onSend,
  allowInternal = false,
  notice,
  sendLabel,
  placeholder,
}: ThreadProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const action = useAction();
  const toast = useToast();

  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);

  return (
    <Card title={t("ticket.thread.title")}>
      {messages.length === 0 ? (
        <EmptyState title={t("ticket.thread.empty")} />
      ) : (
        <ol className="hardauto-support__thread">
          {messages.map((message) => (
            <li
              key={message.id}
              className="hardauto-support__message"
              data-author={message.author}
              data-internal={message.is_internal ? "true" : undefined}
            >
              <div className="hardauto-support__message-head">
                <span className="hardauto-support__message-author">
                  {message.author_name || t(`author.${message.author}`)}
                </span>
                <span className="hardauto-support__message-at">
                  {moment(message.created_at, locale)}
                </span>
                {message.is_internal ? (
                  <Badge tone="warning">{t("ticket.thread.internal")}</Badge>
                ) : null}
              </div>
              {/* `pre-wrap` in the stylesheet: a customer describing a fault
                  writes in paragraphs, and collapsing them makes a
                  description unreadable exactly when it matters. */}
              <p className="hardauto-support__message-body">{message.body}</p>
            </li>
          ))}
        </ol>
      )}

      {notice}

      {onSend ? (
        <div className="hardauto-support__composer">
          <Field label={t("ticket.thread.label")}>
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                placeholder={
                  placeholder ??
                  (internal
                    ? t("ticket.thread.internalPlaceholder")
                    : t("ticket.thread.placeholder"))
                }
                value={body}
                onChange={(event) => setBody(event.target.value)}
              />
            )}
          </Field>

          {allowInternal ? (
            <Checkbox
              label={t("ticket.thread.toggle")}
              checked={internal}
              onChange={(event) => setInternal(event.target.checked)}
            />
          ) : null}

          <Button
            variant="primary"
            busy={action.isBusy("send")}
            busyLabel={t("ticket.thread.send")}
            disabled={!body.trim()}
            onClick={() =>
              void action.run("send", async () => {
                try {
                  await onSend(body.trim(), { internal });
                  setBody("");
                  setInternal(false);
                  toast.show(t("ticket.thread.sent"), { tone: "success" });
                } catch {
                  toast.show(t("ticket.thread.failed"), { tone: "danger" });
                }
              })
            }
          >
            {sendLabel ?? (internal ? t("ticket.thread.sendInternal") : t("ticket.thread.send"))}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
